import React, { useEffect, useState } from "react";

// Material UI
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Components
import ModalHeader from "../../modals/modalHeader";
import { StyledModalRoot } from "../../styled/modal";
import { STRING_NOT_NULL_HELPER } from "../../../common/helpersTxt";
import { validateString } from "../../../common/validators";

export default function CreateOrEditAgentModal({
  agent,
  show,
  closeModal,
  createAgent,
  editAgent,
  availableTools,
}) {
  const theme = useTheme();
  const isEditing = !!agent;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [instructions, setInstructions] = useState("");
  const [allowedActions, setAllowedActions] = useState([]);
  const [maxActionsPerHour, setMaxActionsPerHour] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (agent) {
      setFirstName(agent.first_name || "");
      setLastName(agent.last_name || "");
      setUsername(agent.username || "");
      setInstructions(agent.profile?.instructions || "");
      setAllowedActions(agent.profile?.allowed_actions || []);
      setMaxActionsPerHour(agent.profile?.max_actions_per_hour || 100);
      setIsActive(agent.profile?.is_active ?? true);
    }
  }, [agent]);

  const handleToggleTool = (toolName) => {
    setAllowedActions((prev) =>
      prev.includes(toolName)
        ? prev.filter((t) => t !== toolName)
        : [...prev, toolName]
    );
  };

  const handleToggleDomain = (domain) => {
    const domainTools = availableTools
      .filter((t) => t.domain === domain)
      .map((t) => t.name);

    const allSelected = domainTools.every((t) => allowedActions.includes(t));

    if (allSelected) {
      setAllowedActions((prev) => prev.filter((t) => !domainTools.includes(t)));
    } else {
      setAllowedActions((prev) => [
        ...new Set([...prev, ...domainTools]),
      ]);
    }
  };

  const handleSelectAll = () => {
    const allToolNames = availableTools.map((t) => t.name);
    const allSelected = allToolNames.every((t) => allowedActions.includes(t));

    if (allSelected) {
      setAllowedActions([]);
    } else {
      setAllowedActions(allToolNames);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);

    const nameValid = validateString(firstName);
    const usernameValid = validateString(username);
    const passwordValid = isEditing || validateString(password);

    if (!nameValid || !usernameValid || !passwordValid) {
      return;
    }

    const agentObj = {
      username,
      first_name: firstName,
      last_name: lastName,
      instructions,
      allowed_actions: allowedActions,
      max_actions_per_hour: maxActionsPerHour,
      is_active: isActive,
    };

    if (password) {
      agentObj.password = password;
    }

    if (isEditing) {
      editAgent(agentObj);
    } else {
      agentObj.password = password;
      createAgent(agentObj);
    }
  };

  // Group tools by domain
  const toolsByDomain = availableTools.reduce((acc, tool) => {
    if (!acc[tool.domain]) acc[tool.domain] = [];
    acc[tool.domain].push(tool);
    return acc;
  }, {});

  const allToolNames = availableTools.map((t) => t.name);
  const allSelected = allToolNames.length > 0 && allToolNames.every((t) => allowedActions.includes(t));

  return (
    <Modal
      open={show}
      onClose={closeModal}
      closeAfterTransition
      sx={{ zIndex: 5000 }}
    >
      <Fade in={show}>
        <StyledModalRoot width={700}>
          <ModalHeader
            label={isEditing ? "Editar Agente IA" : "Novo Agente IA"}
            margin={0}
            onClose={closeModal}
          />
          <Box
            sx={{
              p: 4,
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {/* Basic info */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                label="Nome"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={!validateString(firstName) && submitted}
                helperText={
                  !validateString(firstName) && submitted
                    ? STRING_NOT_NULL_HELPER
                    : ""
                }
              />
              <TextField
                size="small"
                variant="outlined"
                label="Sobrenome"
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>

            <TextField
              size="small"
              variant="outlined"
              label="Usuário"
              fullWidth
              required
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              error={!validateString(username) && submitted}
              helperText={
                !validateString(username) && submitted
                  ? STRING_NOT_NULL_HELPER
                  : ""
              }
              disabled={isEditing}
              sx={{ mb: 2 }}
              inputProps={{
                autoComplete: "new-agent-username",
                form: { autoComplete: "off" },
              }}
            />

            <TextField
              size="small"
              variant="outlined"
              label={isEditing ? "Nova Senha (deixe vazio para manter)" : "Senha"}
              fullWidth
              required={!isEditing}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!isEditing && !validateString(password) && submitted}
              helperText={
                !isEditing && !validateString(password) && submitted
                  ? STRING_NOT_NULL_HELPER
                  : ""
              }
              sx={{ mb: 2 }}
              inputProps={{
                autoComplete: "new-agent-password",
                form: { autoComplete: "off" },
              }}
            />

            <TextField
              size="small"
              variant="outlined"
              label="Instruções do Agente"
              fullWidth
              multiline
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Descreva as instruções e comportamento do agente..."
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                size="small"
                variant="outlined"
                label="Limite de ações/hora"
                type="number"
                value={maxActionsPerHour}
                onChange={(e) =>
                  setMaxActionsPerHour(parseInt(e.target.value) || 0)
                }
                sx={{ width: 200 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Agente ativo"
              />
            </Box>

            {/* Tools selection */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Ferramentas Permitidas
                  <Chip
                    label={`${allowedActions.length}/${availableTools.length}`}
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 22 }}
                  />
                </Typography>
                <Button size="small" onClick={handleSelectAll}>
                  {allSelected ? "Desmarcar Todas" : "Selecionar Todas"}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se nenhuma ferramenta for selecionada, o agente terá acesso a
                todas.
              </Typography>

              {Object.entries(toolsByDomain).map(([domain, tools]) => {
                const domainSelected = tools.every((t) =>
                  allowedActions.includes(t.name)
                );
                const domainPartial =
                  !domainSelected &&
                  tools.some((t) => allowedActions.includes(t.name));

                return (
                  <Accordion
                    key={domain}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                      borderRadius: "8px !important",
                      mb: 1,
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={domainSelected}
                          indeterminate={domainPartial}
                          onChange={() => handleToggleDomain(domain)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {domain}
                        </Typography>
                        <Chip
                          label={`${
                            tools.filter((t) =>
                              allowedActions.includes(t.name)
                            ).length
                          }/${tools.length}`}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                      <FormGroup>
                        {tools.map((tool) => (
                          <FormControlLabel
                            key={tool.name}
                            control={
                              <Checkbox
                                checked={allowedActions.includes(tool.name)}
                                onChange={() => handleToggleTool(tool.name)}
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {tool.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {tool.description}
                                </Typography>
                              </Box>
                            }
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </FormGroup>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              {isEditing ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </StyledModalRoot>
      </Fade>
    </Modal>
  );
}
